import { api } from '@/lib/api';

export interface Professional {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  defaultDuration: number;
  isActive: boolean;
  /** Story 67.9: serviços que a profissional faz (lista própria, fora da regra de comissão). */
  serviceLinks?: { serviceId: string; serviceName: string | null }[];
  /** 65.46: presente quando o cadastro emitiu convite — diz se o e-mail saiu. */
  inviteDelivery?: 'sent' | 'not_sent';
  createdAt: string;
}

export interface ProfessionalListResult {
  data: Professional[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ProfessionalQueryParams {
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateProfessionalData {
  name: string;
  email: string;
  phone?: string;
  defaultDuration?: number;
}

export interface UpdateProfessionalData extends Partial<CreateProfessionalData> {}

export interface ScheduleDay {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

export interface ProfessionalBlock {
  id: string;
  date: string;
  startTime: string | null;
  endTime: string | null;
  reason: string | null;
  blockGroupId?: string | null;
  // 65.62: quem criou e se quem está vendo pode remover (regra do servidor).
  createdById?: string | null;
  reservedBy?: 'clinic' | 'professional' | 'unknown';
  canRemove?: boolean;
  createdAt: string;
}

export interface CreateBlockGroupData {
  requestId: string;
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  reason?: string;
}

export interface CreateBlockData {
  date: string;
  startTime?: string;
  endTime?: string;
  reason?: string;
}

export async function getProfessionals(params: ProfessionalQueryParams = {}): Promise<ProfessionalListResult> {
  const { data } = await api.get<ProfessionalListResult>('/professionals', { params });
  return data;
}

export async function getAllProfessionals(
  params: Omit<ProfessionalQueryParams, 'page' | 'limit'> = {},
): Promise<Professional[]> {
  const limit = 100;
  let page = 1;
  const all: Professional[] = [];
  while (page <= 50) {
    const result = await getProfessionals({ ...params, page, limit });
    all.push(...result.data);
    if (page >= result.totalPages || result.data.length === 0) break;
    page += 1;
  }
  return all;
}

export async function createProfessional(dto: CreateProfessionalData): Promise<Professional> {
  const { data } = await api.post<Professional>('/professionals', dto);
  return data;
}

/** 65.46: reenviar o convite de quem ainda não aceitou (permissão de cadastrar profissional). */
export async function resendProfessionalInvite(id: string): Promise<{ inviteDelivery: 'sent' | 'not_sent' }> {
  const { data } = await api.post<{ inviteDelivery: 'sent' | 'not_sent' }>(`/professionals/${id}/resend-invite`, {});
  return data;
}

export async function updateProfessional(id: string, dto: UpdateProfessionalData): Promise<Professional> {
  const { data } = await api.patch<Professional>(`/professionals/${id}`, dto);
  return data;
}

export async function deactivateProfessional(id: string): Promise<{ message: string }> {
  const { data } = await api.delete<{ message: string }>(`/professionals/${id}`);
  return data;
}

export async function activateProfessional(id: string): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(`/professionals/${id}/activate`);
  return data;
}

export async function getSchedule(id: string): Promise<ScheduleDay[]> {
  const { data } = await api.get<ScheduleDay[]>(`/professionals/${id}/schedule`);
  return data;
}

// RC-132: a linha que volta do servidor traz campos dele (id, tenantId, datas) e a tela a
// devolve inteira; o PUT aceita só o contrato.
export async function updateSchedule(id: string, schedule: { days: ScheduleDay[] }): Promise<ScheduleDay[]> {
  const schedules = schedule.days.map(({ dayOfWeek, startTime, endTime, isActive }) => ({ dayOfWeek, startTime, endTime, isActive }));
  const { data } = await api.put<ScheduleDay[]>(`/professionals/${id}/schedule`, { schedules });
  return data;
}

export async function getBlocks(id: string): Promise<ProfessionalBlock[]> {
  const { data } = await api.get<ProfessionalBlock[]>(`/professionals/${id}/blocks`);
  return data;
}

export async function createBlock(id: string, dto: CreateBlockData): Promise<ProfessionalBlock> {
  const { data } = await api.post<ProfessionalBlock>(`/professionals/${id}/blocks`, dto);
  return data;
}

export async function deleteBlock(id: string, blockId: string): Promise<{ message: string }> {
  const { data } = await api.delete<{ message: string }>(`/professionals/${id}/blocks/${blockId}`);
  return data;
}

export async function createBlockGroup(id: string, dto: CreateBlockGroupData) {
  const { data } = await api.post(`/professionals/${id}/block-groups`, dto);
  return data;
}

export async function deleteBlockGroup(id: string, blockGroupId: string): Promise<{ message: string }> {
  const { data } = await api.delete<{ message: string }>(`/professionals/${id}/block-groups/${blockGroupId}`);
  return data;
}
